import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface Profile {
  id: string;
  email: string;
  email_canonical: string;
  contact_email: string | null;
  credits: number;
  plan: 'free' | 'trial' | 'pro' | 'executive';
  pro_expires_at: string | null;
  subscription_cancel_at: string | null;
  subscription_platform: 'stripe' | 'ios' | null;
  preferred_language: string;
  preferred_voice: string;
  created_at: string;
  first_name: string | null;
  notify_new_episodes_push: boolean;
  notify_summary_ready_push: boolean;
  notify_new_episodes_email: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean | null;
  sessionExpired: boolean;
  isResettingPassword: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, firstName?: string, referredBy?: string, preferredLanguage?: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (returnTo?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  clearPasswordReset: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  // Track if we've done the initial admin check to avoid re-checking on token refreshes
  const adminCheckDoneRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  const wasLoggedInRef = useRef(false);
  const isIntentionalSignOutRef = useRef(false);
  const isResettingPasswordRef = useRef(false);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, email_canonical, contact_email, credits, plan, pro_expires_at, subscription_cancel_at, subscription_platform, preferred_language, preferred_voice, created_at, first_name')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      return null;
    }
    
    return data as Profile | null;
  };

  const checkAdminRole = async (accessToken: string) => {
    try {
      const response = await supabase.functions.invoke('admin-check-role', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      setIsAdmin(!!response.data?.isAdmin);
    } catch {
      setIsAdmin(false);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle password recovery - set flag and redirect to reset page
        if (event === 'PASSWORD_RECOVERY') {
          isResettingPasswordRef.current = true;
          setIsResettingPassword(true);
          // Skip redirect if already on /reset-password (avoids reload loop after PKCE exchange)
          if (window.location.pathname !== '/reset-password') {
            setTimeout(() => {
              window.location.href = '/reset-password';
            }, 0);
          }
          return;
        }
        
        // During password reset, suppress normal auth state updates
        // so the user isn't treated as "logged in" and redirected away
        if (isResettingPasswordRef.current && event === 'SIGNED_IN') {
          return;
        }
        
        // Detect involuntary sign-out (session expired, not user-initiated)
        if (event === 'SIGNED_OUT' && wasLoggedInRef.current && !isIntentionalSignOutRef.current) {
          setSessionExpired(true);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Track that user was logged in
          wasLoggedInRef.current = true;
          
          // Check if this is a different user than before
          const isDifferentUser = lastUserIdRef.current !== session.user.id;
          
          // Defer profile fetch to avoid deadlock
          setTimeout(() => {
            fetchProfile(session.user.id).then(setProfile);
          }, 0);
          
          // Only check admin role if:
          // 1. We haven't checked yet, OR
          // 2. This is a different user
          if (!adminCheckDoneRef.current || isDifferentUser) {
            setTimeout(() => {
              checkAdminRole(session.access_token);
              adminCheckDoneRef.current = true;
              lastUserIdRef.current = session.user.id;
            }, 0);
          }
        } else {
          setProfile(null);
          setIsAdmin(null);
          adminCheckDoneRef.current = false;
          lastUserIdRef.current = null;
        }
      }
    );

    // THEN check for existing session. A stale/invalid refresh token (e.g.
    // the user was signed out server-side) can make this reject instead of
    // resolve — without a catch, `loading` would stay true forever and
    // RequireAuth would never redirect to /login. Fall through to
    // logged-out on either a rejection or a hang (stuck cross-tab auth lock,
    // stalled network request) so the UI is never stuck.
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      setLoading(false);
    }, 8000);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        settled = true;
        clearTimeout(timeoutId);
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          fetchProfile(session.user.id).then(setProfile);
          checkAdminRole(session.access_token);
          adminCheckDoneRef.current = true;
          lastUserIdRef.current = session.user.id;
        }
        setLoading(false);
      })
      .catch(() => {
        settled = true;
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    // Same failure mode as getSession() above: a stalled network request or
    // stuck cross-tab auth lock can make this hang instead of reject, leaving
    // the login button spinning forever with no error surfaced. Race it
    // against a timeout so the UI always gets a resolution.
    const timeout = new Promise<{ error: Error }>((resolve) => {
      setTimeout(() => resolve({ error: new Error('Sign in timed out. Please check your connection and try again.') }), 15000);
    });

    try {
      const { error } = await Promise.race([
        supabase.auth.signInWithPassword({ email, password }),
        timeout,
      ]);
      return { error: error as Error | null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, preferredLanguage?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/library`,
        data: {
          client_platform: 'web',
          ...(firstName ? { first_name: firstName } : {}),
          ...(preferredLanguage ? { preferred_language: preferredLanguage } : {}),
        },
      },
    });

    // Fire-and-forget sync to Loops for new signups.
    // NOTE: this path is legacy — the web app no longer accepts new signups
    // in production. We tag it `clientPlatform: 'web'` explicitly so any
    // signup that does come through is distinguishable from iOS.
    if (!error && data?.user) {
      supabase.functions.invoke('sync-to-loops', {
        body: {
          action: 'signup',
          email,
          firstName: firstName || undefined,
          userId: data.user.id,
          plan: 'free',
          preferredLanguage: preferredLanguage || 'en',
          clientPlatform: 'web',
        }
      }).catch(err => console.error('[Loops] Signup sync failed:', err));

      // Fire-and-forget internal notification — DISABLED: Loops already sends
      // a new-signup notification, so this Resend email is redundant. Uncomment
      // to restore.
      // supabase.functions.invoke('send-internal-notification', {
      //   body: {
      //     type: 'signup',
      //     email,
      //     firstName: firstName || undefined,
      //   }
      // }).catch(() => {});
    }

    return { error: error as Error | null };
  };

  const signInWithGoogle = async (returnTo?: string) => {
    // If returnTo is provided, redirect to login page with the returnTo param
    // so the claiming logic in Login.tsx can handle it
    const redirectPath = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/library';
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${redirectPath}`,
      },
    });
    return { error: error as Error | null };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Mark as intentional so onAuthStateChange doesn't show session expired modal
    isIntentionalSignOutRef.current = true;
    
    // Prefer local sign-out so we always clear browser state even if the server session
    // is already gone (common in "session_not_found" cases).
    try {
      // supabase-js supports 'local'|'global' but types may lag; keep it runtime-safe.
      await (supabase.auth.signOut as any)({ scope: 'local' });
    } catch {
      // Silently fail - will be handled by storage cleanup
    }

    // Hard fallback: clear any persisted auth tokens in storage.
    try {
      for (const key of Object.keys(window.localStorage)) {
        if (key.startsWith('sb-') && key.includes('-auth-token')) {
          window.localStorage.removeItem(key);
        }
      }
    } catch {
      // Silently fail - best effort cleanup
    }

    setUser(null);
    setSession(null);
    setProfile(null);
    setIsAdmin(null);
    setSessionExpired(false);
    adminCheckDoneRef.current = false;
    lastUserIdRef.current = null;
    wasLoggedInRef.current = false;
    isIntentionalSignOutRef.current = false;

  };

  const clearPasswordReset = () => {
    isResettingPasswordRef.current = false;
    setIsResettingPassword(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      profile, 
      loading, 
      isAdmin,
      sessionExpired,
      isResettingPassword,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      refreshProfile,
      resetPassword,
      clearPasswordReset,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Returns `{ user, session, isReady }` where `isReady` only flips true after
 * `supabase.auth.getSession()` has resolved at least once — i.e. the session
 * has been restored from storage. Use this to gate any RLS-protected query
 * so it never fires with `auth.uid()` still null.
 *
 * Independent of `AuthContext.loading` on purpose: it reads the session
 * directly so it can't be tricked by an `INITIAL_SESSION` event that fires
 * before storage hydration completes.
 */
export function useAuthReady() {
  const [state, setState] = useState<{ user: User | null; session: Session | null; isReady: boolean }>({
    user: null,
    session: null,
    isReady: false,
  });

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      setState({ user: session?.user ?? null, session, isReady: true });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      // Keep isReady=true once it has flipped; only update user/session afterwards.
      setState((prev) => ({
        user: session?.user ?? null,
        session,
        isReady: prev.isReady,
      }));
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return state;
}
